import React from "react";
import { Tooltip, Icon, Box, TooltipProps } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";

/**
 * HelpTip — standardised tooltip wrapper for the LeadFlow module.
 *
 * Two modes:
 *   1. Wrap: `<HelpTip label="…">{children}</HelpTip>` — wraps an existing element
 *      with a Chakra Tooltip using the module's standard defaults.
 *   2. Icon: `<HelpTip label="…" asIcon />` — drops a small (?) info-icon next to
 *      a plain label / heading where wrapping the text itself is awkward.
 *
 * Chakra `Tooltip` requires a single React child that can accept a ref — it won't
 * work on raw text or fragments. For primitive children, wrap in `<Box as="span">`.
 *
 * Copy style (module convention):
 *   - One sentence, verb-first, ≤140 chars.
 *   - Destructive actions include the consequence: "Archive this lead. Data stays;
 *     hidden from default views."
 *   - Status badges: "INVOICED — a Bill has been issued; pledger hasn't paid yet."
 *   - Public-page tooltips: warmer tone, explain why-it-matters for the visitor.
 */

interface HelpTipProps extends Omit<TooltipProps, "label" | "children"> {
  label: string;
  children?: React.ReactNode;
  /** Render a small info-icon you can drop next to a label — no children needed. */
  asIcon?: boolean;
  /** Icon size when asIcon=true. Defaults to 3. */
  iconSize?: number | string;
}

export const HelpTip: React.FC<HelpTipProps> = ({
  label,
  children,
  asIcon,
  iconSize = 3,
  ...rest
}) => {
  const tooltipProps: Partial<TooltipProps> & { label: string } = {
    label,
    placement: "top",
    hasArrow: true,
    openDelay: 300,
    fontSize: "xs",
    maxW: "280px",
    ...rest,
  };

  if (asIcon) {
    return (
      <Tooltip {...tooltipProps}>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          ml={1}
          cursor="help"
          verticalAlign="middle"
        >
          <Icon as={InfoOutlineIcon} boxSize={iconSize} color="gray.400" />
        </Box>
      </Tooltip>
    );
  }

  return <Tooltip {...tooltipProps}>{children as any}</Tooltip>;
};

export default HelpTip;
